import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { Maybe } from '../../core'
import { Routes } from '../../routes';
import { InputState, input } from '../../components/input';
import { Team } from '../../entities';
import { WidgetService } from '../../services/widget';
import { Metadata } from '@clipr/lib';
import { CopyJobWindowState } from '../../components/jobs';
import { SearchBarState } from '../../components/search/searchBarState';
import { SearchService } from '../../services/search/searchService';

export class SearchResultsState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'widgetServiceListener'>(this, {
      pageSize: observable,
      teamId: observable,
      queryString: observable,
      isFocused: observable,
      searchService: computed,
      metadataFilter: computed,
      tagsFilter: computed,
      team: computed,
      handleFocus: action,
      setPageSize: action,
      widgetServiceListener: action,
      mounted: action,
      unmounted: action,
      reset: action,
      loadValue: action,
      submit: action,
      searchUrl: computed,
      showSuggestions: computed,
      isWidgetMode: computed,
    });

    this.model = input(this, {
      name: 'searchInput',
      onChange: () => this.searchService.handleChange({ value: this.model.value ?? '' }),
      onFocus: this.handleFocus,
      onBlur: () => { setTimeout(action(() => { this.isFocused = false; }), 200) }
    });

    this.searchBarState = new SearchBarState(this.store, 
    { 
      teamId: () => this.teamId,
      searchMode: 'search'
     });
  }

  readonly searchBarState: SearchBarState;
  readonly model: InputState;

  pageSize: number = 20;
  teamId: Maybe<string> = null;

  queryString: Maybe<string> = ''; // URL search query param
  isFocused: boolean = false;
  
  get searchService(): SearchService {
    return this.store.searchService;
  }

  get metadataFilter(): Partial<Metadata> | null {
    return this.store.widgetService.metadataFilter;
  }

  get tagsFilter(): string[] | null {
    return this.store.widgetService.tagsFilter;
  }

  get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId) ?? null;
  }

  handleFocus = () => {
    this.isFocused = true;
    const { hasSuggestions, handleChange } = this.searchService;
    if (!hasSuggestions) {
      handleChange({ value: this.model.value ?? '' });
    }
  }

  async setPageSize(pageSize: number) {
    if (pageSize !== this.pageSize) {
      this.pageSize = pageSize;
      await this.searchService.debouncedSearch(this.pageSize, this.model.value);
    }
  }

  private widgetServiceListener = (msg: Message<WidgetService>) => {
    // This listener handles loginRequired from WidgetService on auth errors,
    // previously caught from the raw auth0 message
    switch (msg.type) {
      case 'loginRequired':
        this.store.router.history.back();
        break;
      default:
        break;
    }
  }

  async mounted(teamId?: Maybe<string>) {
    // TODO: replace direct URL parsing with route param injection
    const query = new URLSearchParams(window.location.search);
    const searchParam = query.get('searchKey');
    const teamIdParam = query.get('teamId');
    // Set the search value at init for shareable search urls
    this.model.value = searchParam || '';
    this.teamId = teamId || teamIdParam;
    this.queryString = searchParam;

    const { searchService } = this.store;
    searchService.setTeamId(this.teamId);
    searchService.fetchData(this.pageSize, this.model.value);
    this.searchBarState.setSearchMode('search');
    this.bindExternalListeners();
  }

  private bindExternalListeners() {
    const { store } = this;
    const { renameJobWindow, deleteJobWindow, copyJobWindow, removeJobFromTeamWindow, removeJobFromCliprWindow, uploadThumbnailWindow, videoDetailsWindow } = store;
    renameJobWindow.listen(
      this.mutationWindowListener);
    copyJobWindow.listen(
      this.copyJobWindowListener);
    deleteJobWindow.listen(
      this.mutationWindowListener);
    removeJobFromTeamWindow.listen(
      this.mutationWindowListener);
    removeJobFromCliprWindow.listen(
      this.mutationWindowListener);
    uploadThumbnailWindow.listen(
      this.mutationWindowListener);
    videoDetailsWindow.listen(
      this.mutationWindowListener);
  }

  private unbindExternalListeners() {
    const { store } = this;
    const { renameJobWindow, deleteJobWindow, copyJobWindow, removeJobFromTeamWindow, removeJobFromCliprWindow, uploadThumbnailWindow, videoDetailsWindow } = store;
    renameJobWindow.unlisten(
      this.mutationWindowListener);
    copyJobWindow.unlisten(
      this.copyJobWindowListener);
    deleteJobWindow.unlisten(
      this.mutationWindowListener);
    removeJobFromTeamWindow.unlisten(
      this.mutationWindowListener);
    removeJobFromCliprWindow.unlisten(
      this.mutationWindowListener);
    uploadThumbnailWindow.unlisten(
      this.mutationWindowListener);
    videoDetailsWindow.unlisten(
      this.mutationWindowListener);
  }

  private copyJobWindowListener = action(async (msg: Message<CopyJobWindowState>) => {
    switch (msg.type) {
      case 'success':
        // TODO: move job data to jobManager to eliminate the dual catalog inconsistency
        this.searchService.jobCatalog.insertJob(msg.payload.job);
        break;
    }
  });

  private mutationWindowListener = action(async (msg: Message) => {
    switch (msg.type) {
      case 'JobDeleted':
      case 'JobRemoved':
      case 'ThumbnailUpdated':
      case 'JobUpdated':
      case 'JobRenamed':
        this.searchService.search(this.pageSize, this.model.value);
        break;
    }
  });

  unmounted() {
    this.reset();
    this.queryString = null;
    this.teamId = null;
    this.searchService.setTeamId(null);
    this.searchService.jobCatalog.popularVideosLookup.clear();
    this.unbindExternalListeners();
  }

  reset = () => {
    const { pathname } = window.location;
    if (
      pathname === Routes.search ||
      pathname === Routes.librarySearchWidget()) {
      return;
    }
    
    this.model.clear();
    this.searchBarState.reset();
    this.searchService.clearSuggestions();
  }

  loadValue = (value?: string | null) => {
    this.model.loadValue(value);
    this.model.handleFocus();
  }

  submit(replaceRoute = false, teamId: string | null) {
    this.teamId = teamId;
    this.queryString = this.model.value;
    this.searchService.setTeamId(teamId);
    this.searchService.clearSuggestions();
    this.isFocused = false;

    const { history } = this.store.routingService;
    if (replaceRoute)
      history.replace(this.searchUrl);
    else
      history.push(this.searchUrl);
  }

  get searchUrl(): string {
    const path = this.isWidgetMode ? Routes.librarySearchWidget() : Routes.search;
    const params = new URLSearchParams();
    if (this.model.value) {
      params.append('searchKey', this.model.value);
    }
    if (this.teamId) {
      params.append('teamId', this.teamId);
    }

    return `${path}${params ? `?${params}` : ''}`;
  }

  get showSuggestions(): boolean {
    return !!(
      this.isFocused &&
      this.searchService.hasSuggestions &&
      !this.searchService.isLoadingSuggestions &&
      this.model.value &&
      this.model.value.length >= 3 &&
      !this.isWidgetMode
    );
  }

  get isWidgetMode(): boolean {
    return this.store.widget.isWidgetMode;
  }
}
